import dermForearm from "../../../assets/medwand/derm-forearm.jpg";
import dermPigmented from "../../../assets/medwand/derm-pigmented.jpg";
import dermErythematous from "../../../assets/medwand/derm-erythematous.jpg";

/**
 * Sample exam readings for the MedWand device tabs.
 *
 * Ported from the backend fixture the sandbox actually returned —
 * Albert-backend/src/integrations/medwand/fixtures/sampleExam.js — so the
 * values, sensor names, modes and units here are MedWand's own, not invented
 * ones. Kept in the normalised shape the panel consumes rather than the raw
 * payload shape, since the parsing (unit out of sensorSettings, location, skip
 * reason) belongs to the backend.
 *
 * This is one sandbox patient's exam, NOT the client being viewed. Every tab
 * rendered from this file must carry the "Sample data" badge — clinical numbers
 * that look real but aren't are worse than an empty panel, because after a
 * screenshot nobody can tell which was which.
 *
 * Drop this once /medwand/clients/:id/summary returns each sensor group.
 */

// Dated forward from the fixture's own 2025-01-23 run so the sample reads as a
// recent exam. Kept as a plain local string with no timezone suffix, matching
// how MedWand sends its *Utc timestamps.
export const MOCK_CAPTURED_AT = "2026-08-30T15:30:00";

export const MOCK_EXAM_META = {
  doctorName: "Dr. Charlotte Myers",
  examType: "Remote",
  durationMinutes: "9",
};

// Sensor groups mirror the device tabs. "sensors" is the catch-all, matching
// the way MedWand's own device screen groups everything that is not one of the
// four named instruments.
export const MOCK_GROUP_READINGS = {
  sensors: [
    { sensor: "BloodPressure", raw: "120/80", unit: "mmHg" },
    { sensor: "PulseRate", raw: "101", unit: "bpm" },
    { sensor: "Respiratory", raw: "25.0", unit: "br/min" },
    { sensor: "Glucometer", raw: "150", unit: "mg/dL" },
    { sensor: "WeightScale", raw: "200.0", unit: "lb" },
    { sensor: "HeightScale", raw: "65.0", unit: "in" },
    { sensor: "WaistSize", raw: "45.0", unit: "in" },
    { sensor: "PainLevel", raw: "2", unit: "/10" },
    { sensor: "CholesterolTotal", raw: "600", unit: "mg/dL" },
    { sensor: "CholesterolLdl", raw: "500", unit: "mg/dL" },
    { sensor: "CholesterolHdl", raw: "400", unit: "mg/dL" },
    { sensor: "GTTInitial", raw: "100", unit: "mg/dL" },
    { sensor: "GTT1Hour", raw: "200", unit: "mg/dL" },
    { sensor: "GTT2Hour", raw: "300", unit: "mg/dL" },
    { sensor: "SpirometerFev1", raw: "22.0", unit: "L" },
    { sensor: "SpirometerFvc", raw: "33.0", unit: "L" },
    { sensor: "SpirometerPef", raw: "11", unit: "L/min" },
    { sensor: "Ecg", raw: "", unit: "", isMedia: true, mediaType: "image/png", captures: 2 },
  ],
  thermometer: [{ sensor: "Thermometer", raw: "97.2", unit: "°F", sensorMode: "Default" }],
  oximeter: [
    { sensor: "SpO2", raw: "94", unit: "%" },
    { sensor: "PulseRate", raw: "101", unit: "bpm" },
  ],
  // Six recordings plus one the clinician skipped — the skip carries their
  // reason, which is the whole point of showing it rather than hiding the row.
  // One recording per auscultation site. The fixture repeats each mode two or
  // three times, which rendered as identical boxes; the repeats carry no extra
  // information here. The skipped entry stays — it is the only row that shows
  // the clinician's reason, which is the reason for showing skips at all.
  stethoscope: [
    {
      sensor: "Stethoscope",
      sensorMode: "Heart",
      isMedia: true,
      mediaType: "audio/mp3",
      description: "Heart sounds, 18 s recording.",
    },
    {
      sensor: "Stethoscope",
      sensorMode: "Lungs",
      location: "UpperMidLeftThorax",
      isMedia: true,
      mediaType: "audio/mp3",
      description: "Lung sounds, 22 s recording.",
    },
    {
      sensor: "Stethoscope",
      sensorMode: "Abdominal",
      isMedia: true,
      mediaType: "audio/mp3",
      description: "Abdominal sounds, 21 s recording.",
    },
    {
      sensor: "Stethoscope",
      sensorMode: "Abdominal",
      location: "UpperMidLeftThorax",
      skipped: true,
      skipReason:
        "I could not take this recording because of a problem that I encountered while trying to take the recording.",
    },
  ],
  // Three captures with real images. The descriptions state only what is
  // visible in the frame — no impression, no diagnosis; reading one into a
  // sample image would put words in a clinician's mouth.
  camera: [
    {
      sensor: "Otoscope",
      sensorMode: "Dermatoscope",
      location: "LeftForearm",
      isMedia: true,
      mediaType: "image/jpeg",
      raw: dermForearm,
      description:
        "Healing pressure sore — pale re-epithelialised centre with surrounding discolouration, about 8 mm. Intact skin, no open wound.",
    },
    {
      sensor: "Otoscope",
      sensorMode: "Dermatoscope",
      location: "RightShoulder",
      isMedia: true,
      mediaType: "image/jpeg",
      raw: dermPigmented,
      description: "Uniform brown macule, about 4 mm against the millimetre scale.",
    },
    {
      sensor: "Otoscope",
      sensorMode: "Dermatoscope",
      location: "UpperBack",
      isMedia: true,
      mediaType: "image/jpeg",
      raw: dermErythematous,
      description: "Well-defined pink-red papule, about 5 mm against the millimetre scale.",
    },
  ],
};

// Which tab a sensor belongs to. Used for real readings as well as the sample
// set, so a live feed lands in the same place the sample did.
export const groupForSensor = (sensor) => {
  const s = String(sensor || "");
  if (/thermo/i.test(s)) return "thermometer";
  if (/spo2|oximet|perfusion/i.test(s)) return "oximeter";
  if (/stethoscope/i.test(s)) return "stethoscope";
  if (/otoscope|dermatoscope|camera/i.test(s)) return "camera";
  return "sensors";
};
